import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import DatePickerReact from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { createCampaign, countCampaignAudience } from '../../../store/messaging/actions';

const CAMPAIGN_ID = /^[A-Za-z0-9_-]+$/;

// The picker gives a Date in the browser's zone; the API wants the same wall-clock digits as UTC
// (10:00 on the user's clock → …T10:00:00Z).
const toLocalSendAt = (d) => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes())).toISOString();

const parseList = (s) =>
  s
    .split(',')
    .map((i) => i.trim())
    .filter(Boolean);

const CampaignForm = ({ title, body, onScheduled }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [campaignId, setCampaignId] = useState('');
  const [route, setRoute] = useState('');
  const [sendAt, setSendAt] = useState(null);
  const [platform, setPlatform] = useState('');
  const [langs, setLangs] = useState('');
  const [idleDaysMin, setIdleDaysMin] = useState('');
  const [idleDaysMax, setIdleDaysMax] = useState('');
  const [audienceCount, setAudienceCount] = useState(null);

  const audience = () => {
    const a = {};
    if (platform) a.platform = platform;
    if (langs.trim()) a.langs = parseList(langs);
    if (idleDaysMin !== '') a.idleDaysMin = Number(idleDaysMin);
    if (idleDaysMax !== '') a.idleDaysMax = Number(idleDaysMax);
    return a;
  };

  const canSchedule = CAMPAIGN_ID.test(campaignId) && campaignId !== 'retention' && title.trim() && body.trim() && sendAt;

  const handleCount = async () => {
    try {
      setAudienceCount(await dispatch(countCampaignAudience(audience())));
    } catch (error) {
      alert(error?.response?.data?.message || 'Count failed');
    }
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    if (!canSchedule) return;
    try {
      const data = route.trim() ? { route: route.trim() } : {};
      await dispatch(createCampaign({ campaignId, title, body, data, localSendAt: toLocalSendAt(sendAt), audience: audience() }));
      setCampaignId('');
      setRoute('');
      setSendAt(null);
      setAudienceCount(null);
      alert('Campaign scheduled');
      if (onScheduled) onScheduled();
    } catch (error) {
      alert(error?.response?.data?.message || 'Campaign was not scheduled');
    }
  };

  return (
    // A <div>, not a <form>: the page already wraps title/body in its own <form>, and forms do not nest.
    <div className={classes.form}>
      <TextField
        className={classes.field}
        label="campaign id (A-Z a-z 0-9 _ -)"
        value={campaignId}
        onChange={(e) => setCampaignId(e.target.value)}
        variant="outlined"
      />
      <TextField
        className={classes.field}
        label="data.route (optional: DailyReward | Home | Trading | Market | Profile)"
        value={route}
        onChange={(e) => setRoute(e.target.value)}
        variant="outlined"
      />
      <div className={classes.field}>
        <Typography variant="caption" color="textSecondary">
          Send at (user&apos;s local time)
        </Typography>
        <DatePickerReact selected={sendAt} onChange={setSendAt} showTimeSelect timeIntervals={60} dateFormat="d.MM.yy HH:mm" />
      </div>
      <TextField
        select
        className={classes.field}
        label="platform"
        value={platform}
        onChange={(e) => setPlatform(e.target.value)}
        variant="outlined"
      >
        <MenuItem value="">all</MenuItem>
        <MenuItem value="ios">ios</MenuItem>
        <MenuItem value="android">android</MenuItem>
      </TextField>
      <TextField
        className={classes.field}
        label="languages (comma separated, empty = all)"
        value={langs}
        onChange={(e) => setLangs(e.target.value)}
        variant="outlined"
      />
      <div className={classes.row}>
        <TextField
          className={classes.half}
          label="idle days min"
          type="number"
          value={idleDaysMin}
          onChange={(e) => setIdleDaysMin(e.target.value)}
          variant="outlined"
        />
        <TextField
          className={classes.half}
          label="idle days max"
          type="number"
          value={idleDaysMax}
          onChange={(e) => setIdleDaysMax(e.target.value)}
          variant="outlined"
        />
      </div>
      <Button variant="outlined" size="large" color="primary" type="button" className={classes.button} onClick={handleCount}>
        Count audience{audienceCount !== null ? `: ${audienceCount}` : ''}
      </Button>
      <Button
        variant="contained"
        size="large"
        color="primary"
        type="button"
        disabled={!canSchedule}
        className={classes.button}
        onClick={handleSchedule}
      >
        Schedule campaign
      </Button>
    </div>
  );
};

export default CampaignForm;

const useStyles = makeStyles((theme) => ({
  form: { display: 'flex', flexDirection: 'column' },
  field: { width: '100%', marginTop: theme.spacing(2) },
  row: { display: 'flex', gap: theme.spacing(2), marginTop: theme.spacing(2) },
  half: { width: '50%' },
  button: { width: '100%', marginTop: theme.spacing(2) },
}));
